import React, { useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import {
  Context,
  fetchClasses,
  fetchSubClasses,
  fetchTeacherSubjectsByTeacher,
  fetchSubjects,
  fetchSubjectCategories,
  submitTeacherSubjects,
  deleteTeacherSubject,
  submitSubject,
  updateSubject,
  deleteSubject,
  clearSuccessMessage,
  clearErrorMessage,
  subjectsPath,
  FETCH_TEACHER_SUBJECTS_BY_TEACHER_SUCCESS,
  DELETE_TEACHER_SUBJECT_SUCCESS_MESSAGE,
  DELETE_TEACHER_SUBJECT_FAILURE_MESSAGE,
  SUBMIT_SUBJECT_SUCCESS_MESSAGE,
  SUBMIT_SUBJECT_FAILURE_MESSAGE,
  UPDATE_SUBJECT_SUCCESS_MESSAGE,
  UPDATE_SUBJECT_FAILURE_MESSAGE,
  DELETE_SUBJECT_SUCCESS_MESSAGE,
  DELETE_SUBJECT_FAILURE_MESSAGE,
} from "../../../managerStore";
import { RegistrationSubject } from "../../../state";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import { titles } from "../../../constants/titles";
import { Breadcrumb } from "../../../molecules/Breadcrumb";
import Toast from "../../../molecules/Toast";
import SubjectRegistration from "../../../templates/manager/SubjectRegistration";

const SubjectRegistrationPage = () => {
  const { state, dispatch } = useContext(Context);
  const { teacher_id } = useParams<{ teacher_id: string }>();
  const teacherId = Number(teacher_id);
  const [calling, peep] = useWaitApiCall(state);
  const { currentYear } = useCurrentYear();
  const [registrationSubjects, setRegistrationSubjects] = useState<
    RegistrationSubject[]
  >([]);
  const [teacherName, setTeacherName] = useState("");

  const submitTeacherSubjectsFunc = useCallback(
    (
      teacherId: number,
      subjects: { subject_id: number; klass_ids: number[] }[],
    ) => {
      dispatch(peep(submitTeacherSubjects(teacherId, subjects)));
    },
    [dispatch, peep],
  );

  const deleteTeacherSubjectFunc = useCallback(
    (teacherId: number, id: number) => {
      dispatch(peep(deleteTeacherSubject(teacherId, id)));
    },
    [dispatch, peep],
  );

  const submitSubjectFunc = useCallback(
    (name: string) => {
      dispatch(peep(submitSubject(name)));
    },
    [dispatch, peep],
  );

  const updateSubjectFunc = useCallback(
    (name: string, id: number) => {
      dispatch(peep(updateSubject(name, id)));
    },
    [dispatch, peep],
  );

  const deleteSubjectFunc = useCallback(
    (id: number) => {
      dispatch(peep(deleteSubject(id)));
    },
    [dispatch, peep],
  );

  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchSubjectCategories());
    dispatch(fetchClasses(currentYear));
    dispatch(fetchSubClasses());
    dispatch(fetchTeacherSubjectsByTeacher(teacherId));
  }, [dispatch, teacherId, currentYear]);

  useEffect(() => {
    const successMessages = [
      DELETE_TEACHER_SUBJECT_SUCCESS_MESSAGE,
      SUBMIT_SUBJECT_SUCCESS_MESSAGE,
      UPDATE_SUBJECT_SUCCESS_MESSAGE,
      DELETE_SUBJECT_SUCCESS_MESSAGE,
    ];

    if (successMessages.includes(state.success_message)) {
      Toast.success(state.success_message);
      dispatch(clearSuccessMessage());

      if (state.success_message === DELETE_SUBJECT_SUCCESS_MESSAGE) {
        dispatch(fetchTeacherSubjectsByTeacher(teacherId));
      }
      if (state.success_message === DELETE_TEACHER_SUBJECT_SUCCESS_MESSAGE) {
        dispatch(fetchSubjects());
      }
    }
  }, [state.success_message, dispatch, teacherId]);

  useEffect(() => {
    const failureMessages = [
      DELETE_TEACHER_SUBJECT_FAILURE_MESSAGE,
      SUBMIT_SUBJECT_FAILURE_MESSAGE,
      UPDATE_SUBJECT_FAILURE_MESSAGE,
      DELETE_SUBJECT_FAILURE_MESSAGE,
    ];

    if (failureMessages.includes(state.error)) {
      Toast.error(state.error);
      dispatch(clearErrorMessage());
    }
  }, [state.error, dispatch]);

  useEffect(() => {
    if (state.lastAction === FETCH_TEACHER_SUBJECTS_BY_TEACHER_SUCCESS) {
      const data = state.teacher_subjects_by_teacher.subjects.map(
        ({ subject_id, klasses }) => ({
          subject_id,
          klass_ids: klasses.map(({ klass_id }) => klass_id),
        }),
      );
      setRegistrationSubjects(data);
      setTeacherName(state.teacher_subjects_by_teacher.teacher_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.lastAction]);

  return (
    <div>
      <Breadcrumb
        links={[{ title: titles.subjects, to: subjectsPath }]}
        currentTitle={titles.subjectRegistration}
      />
      <SubjectRegistration
        calling={calling}
        teacherId={teacherId}
        registrationSubjects={registrationSubjects}
        teacherName={teacherName}
        setRegistrationSubjects={setRegistrationSubjects}
        submitTeacherSubjects={submitTeacherSubjectsFunc}
        deleteTeacherSubject={deleteTeacherSubjectFunc}
        submitSubject={submitSubjectFunc}
        updateSubject={updateSubjectFunc}
        deleteSubject={deleteSubjectFunc}
      />
    </div>
  );
};
export default SubjectRegistrationPage;
